import { type Observable } from 'rxjs'
import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  UpdateNotificationPreferencesRequest,
  SetNotificationStatusRequest,
  NotificationResource,
  ListNotificationsRequest,
  NotificationPreferencesResource,
  NotificationRealtimeEventResource,
} from './resources/notification'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Collection of APIs for notifications.
 */
export class NotificationsApi {
  readonly notifications
  readonly preferences

  constructor(options: ApiClientOptions) {
    this.notifications = new NotificationApi(options)
    this.preferences = new NotificationPreferencesApi(options)
  }
}

/**
 * API for notifications.
 */
export class NotificationApi extends ApiClient {
  events$: Observable<NotificationRealtimeEventResource> = this._events()

  list(
    request: ListNotificationsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<NotificationResource>> {
    return this._get('v3/notifications', toURLSearchParams({ ...request }), {
      signal,
    })
  }

  changeStatus(
    request: SetNotificationStatusRequest,
  ): Promise<NotificationResource | null> {
    return this._post(`v3/notifications/${request.id}/status`, request)
  }

  changeStatusBulk(
    request: SetNotificationStatusRequest[],
  ): Promise<NotificationResource[]> {
    return this._post('v3/notifications/status', request)
  }
}

/**
 * API for notification preferences.
 */
export class NotificationPreferencesApi extends ApiClient {
  get(
    workspaceId: string,
    userPublicId: string,
    signal?: AbortSignal,
  ): Promise<NotificationPreferencesResource> {
    return this._get(
      `v3/workspaces/${workspaceId}/members/${userPublicId}/preferences`,
      undefined,
      { signal },
    )
  }

  patch(
    workspaceId: string,
    userPublicId: string,
    request: UpdateNotificationPreferencesRequest,
  ): Promise<NotificationPreferencesResource> {
    return this._patch(
      `v3/workspaces/${workspaceId}/members/${userPublicId}/preferences`,
      request,
    )
  }
}
